/**
 * File → API-payload conversion shared by every upload flow.
 * The API has two file shapes:
 * - note attachments: `{ filename, fileData (raw base64), mimeType, size }`
 * - `UploadFileSchema`-style documents: `{ name, document (base64 DataURL), mimeType }`
 * `FilePayload` carries both encodings so each call site picks what it needs.
 */
export interface FilePayload {
  filename: string;
  /** Raw base64, no `data:` prefix — claim-note attachment shape. */
  fileData: string;
  /** Base64 DataURL — `UploadFileSchema.document` / expense-file shape. */
  dataUrl: string;
  mimeType: string;
  size: number;
}

const FALLBACK_MIME_TYPE = "application/octet-stream";

export async function fileToPayload(file: File): Promise<FilePayload> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  // Chunked to avoid call-stack limits of String.fromCharCode(...bytes) on large files.
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  const fileData = btoa(binary);
  const mimeType = file.type || FALLBACK_MIME_TYPE;

  return {
    filename: file.name,
    fileData,
    dataUrl: `data:${mimeType};base64,${fileData}`,
    mimeType,
    size: file.size,
  };
}
